"use client";
import React, { useState, useEffect, useRef } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { motion, AnimatePresence } from 'framer-motion';

const StickyMenu = ({ items }) => {
  const router = useRouter();
  const pathname = usePathname();
  const [isVisible, setIsVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const menuRef = useRef(null);
  const itemRefs = useRef({});

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollPercentage = (scrollPosition + windowHeight) / documentHeight * 100;
      setIsVisible(scrollPercentage > 20 && scrollPercentage < 90);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const activeItem = itemRefs.current[pathname];
    if (activeItem) {
      scrollItemIntoView(activeItem);
    }
  }, [pathname]);

  const handleItemClick = (href) => {
    router.push(href);
    const clickedItem = itemRefs.current[href];
    if (clickedItem) {
      scrollItemIntoView(clickedItem);
    }
  };

  const scrollItemIntoView = (item) => {
    if (menuRef.current) {
      const container = menuRef.current.querySelector('.scroll-container');
      const itemRect = item.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      if (itemRect.right > containerRect.right) {
        container.scrollLeft += itemRect.right - containerRect.right + 20;
      } else if (itemRect.left < containerRect.left) {
        container.scrollLeft -= containerRect.left - itemRect.left + 20;
      }
    }
  };

  return (
    <AnimatePresence>
      {(isVisible || isMobile) && (
        <motion.div
          ref={menuRef}
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: "spring", bounce: 0.15, duration: 0.5 }}
          className="fixed bottom-0 z-50 left-0 right-0 bg-white/80 backdrop-blur-lg border-t border-blue-100/50"
        >
          <div className="container mx-auto px-4 py-4 overflow-x-auto scroll-container">
            <div className="flex space-x-3 justify-start md:justify-center">
              {items.map((item) => (
                <motion.button
                  key={item.id}
                  ref={(el) => itemRefs.current[item.href] = el}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => handleItemClick(item.href)}
                  className={`
                    relative flex-shrink-0 whitespace-nowrap px-6 py-3 rounded-full text-sm font-medium
                    transition-all duration-300 
                    ${pathname === item.href 
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg hover:shadow-blue-500/25' 
                      : 'bg-white/50 border border-blue-100/50 text-gray-700 hover:border-blue-200 hover:bg-white/80'
                    }
                  `}
                >
                  {pathname === item.href && (
                    <motion.div
                      layoutId="activeBackground"
                      className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 -z-10"
                      transition={{ type: "spring", bounce: 0.15, duration: 0.5 }}
                    />
                  )}
                  <span className="relative z-10">{item.label}</span>
                </motion.button>
              ))}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default StickyMenu;